import { ROLE_MENU_CONFIG, UserRole, MenuFilterConfig, getRouteConfig, hasEmailAccess } from '@/config/rbac.config';

export interface MenuItem {
  titleKey: string;
  url: string;
  items?: SubMenuItem[];
}

export interface SubMenuItem {
  titleKey: string;
  url: string;
  icon?: React.ReactNode;
}

export const getDefaultMenuFilter = (): MenuFilterConfig => ({
  hideItems: ['districtAdmin.title', 'terraceMetricsAdmin.title'],
  hideSubItems: {
    'assessment.title': ['assessment.combinedReport']
  }
});

export const filterMenuByRole = (
  menuItems: MenuItem[], 
  userRole: string | null, 
  isLoading: boolean = false,
  userEmail?: string | undefined
): MenuItem[] => {
  // During loading or for unknown roles, apply default restrictions
  if (isLoading || !userRole || !(userRole in ROLE_MENU_CONFIG)) {
    const defaultConfig = getDefaultMenuFilter();
    return applyMenuFilter(menuItems, defaultConfig, userEmail);
  }

  const config = ROLE_MENU_CONFIG[userRole as UserRole];
  return applyMenuFilter(menuItems, config, userEmail);
};

/**
 * Enhanced menu filtering that considers both user role and UI mode
 * Specifically handles school_champion users with simplified UI mode
 */
export const filterMenuByRoleAndUIMode = (
  menuItems: MenuItem[], 
  userRole: string | null, 
  uiMode: string | null,
  isLoading: boolean = false,
  userEmail?: string | undefined
): MenuItem[] => {
  // First apply role-based filtering
  const roleFilteredItems = filterMenuByRole(menuItems, userRole, isLoading, userEmail);
  
  // If user is school_champion and has simplified UI mode, apply additional filtering
  if (userRole === 'school_champion' && uiMode === 'simplified') {
    return roleFilteredItems.filter(item => {
      // Hide specific menu items for school_champion with simplified UI
      const hiddenItems = [
        'dashboard',
        'guideline.title', 
        'participants.title',
        'groupAdmin.title'
      ];
      return !hiddenItems.includes(item.titleKey);
    });
  }
  
  return roleFilteredItems;
};

const applyMenuFilter = (menuItems: MenuItem[], config: MenuFilterConfig, userEmail?: string | undefined): MenuItem[] => {
  return menuItems
    .filter(item => !config.hideItems.includes(item.titleKey))
    .map(item => {
      const hiddenSubItems = config.hideSubItems[item.titleKey] || [];
      
      if (hiddenSubItems.length > 0 && item.items) {
        return {
          ...item,
          items: item.items.filter(subItem => {
            // Check if this sub-item should be hidden based on role
            const shouldHide = hiddenSubItems.includes(subItem.titleKey);
            
            // Special case: if it's the combined report and user has email-based access, don't hide it
            if (shouldHide && subItem.titleKey === 'assessment.combinedReport') {
              const routeConfig = getRouteConfig('/assessment/combined-report');
              if (routeConfig?.allowedEmails && userEmail) {
                return hasEmailAccess(userEmail, routeConfig.allowedEmails);
              }
            }
            
            return !shouldHide;
          })
        };
      }
      
      return item;
    });
};